import {
  authenticatedFastApiAxiosInstance,
  publicFastApiAxiosInstance,
} from "@/lib/data/http";
import { type APIResponse } from "@/lib/types";
import type { LayoutField } from "@/components/features/matter/file-repo/signature-requests/SignatureRequestPdfLayoutEditor.constants";

export type CreateFirmSignatureRequestV2Input = {
  fileId: string;
  fileIdDestination: string | null;
  expirationDate: string;
  contactConnections: Array<{
    contactId: string;
    label: string;
    signerOrder?: number;
  }>;
  userConnections: Array<{
    userId: string;
    label: string;
    signerOrder?: number;
  }>;
  fields: Array<{
    signerType: "contact" | "user";
    signerId: string;
    type: LayoutField["type"];
    label: string;
    required: boolean;
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
    defaultValue?: string | null;
    placeholder?: string | null;
    validationMaxLength?: number | null;
    validationPattern?: string | null;
    fontFamily?: "Times New Roman" | "Arial" | "Courier New" | null;
    fontSize?: number | null;
  }>;
};

export type CreateFirmSignatureRequestV3Input =
  CreateFirmSignatureRequestV2Input & {
    notifications: {
      contactChannels: Array<"email" | "sms">;
      firmPhoneId?: string;
      notifyUsersByEmail: boolean;
    };
  };

type FirmSignatureRequestV2Data = {
  id: string;
  expirationDate: string;
  contactConnections: Array<{
    contactId: string;
    contactName: string | null;
    contactEmails: Array<{ emailAddress: string | null }>;
    contactPhones: Array<{ id: string; number: string | null }>;
    signatureRequestPasscode: string;
  }>;
  userConnections: Array<{
    signatureRequestPasscode: string;
    user: {
      id: string;
      email: string | null;
      firstName: string | null;
      lastName: string | null;
    } | null;
  }>;
};

export const createFirmSignatureRequestV2 = (
  data: CreateFirmSignatureRequestV2Input,
) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<FirmSignatureRequestV2Data>
    >("firms/v2/signature-requests", data)
    .then((res) => res.data);

export const createFirmSignatureRequestV3 = (
  data: CreateFirmSignatureRequestV3Input,
) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<FirmSignatureRequestV2Data>
    >("firms/v3/signature-requests", data)
    .then((res) => res.data);

export type FirmSignatureRequestPublicV2Data = {
  signatureRequest: {
    id: string;
    firmId: string;
    fileId: string;
    fileIdDestination: string | null;
    expirationDate: string;
    dateModified: string;
    name: string;
    presignedUrlKey: string;
    variables: unknown;
  };
  fields: Array<{
    id: string;
    signerType: "contact" | "user";
    signerId: string;
    type: LayoutField["type"];
    label: string;
    required: boolean;
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
    defaultValue: string | null;
    placeholder: string | null;
    validationMaxLength: number | null;
    validationPattern: string | null;
    fontFamily?: "Times New Roman" | "Arial" | "Courier New" | null;
    fontSize?: number | null;
  }>;
  contactConnections: Array<{
    id: string;
    dateModified: string;
    label: string;
    signatureRequestComplete: boolean;
    pendingSigner: boolean;
    signerOrder: number | null;
  }>;
  userConnections: Array<{
    dateModified: string;
    label: string;
    signatureRequestComplete: boolean;
    signerOrder: number | null;
    pendingSigner: boolean;
  }>;
  requestingContactConnection: {
    dateModified: string;
    label: string;
    signatureRequestComplete: boolean;
    signerOrder: number | null;
    pendingSigner: boolean;
    signatureRequestPasscode: string;
  } | null;
  requestingUserConnection: {
    dateModified: string;
    label: string;
    signatureRequestComplete: boolean;
    signerOrder: number | null;
    pendingSigner: boolean;
    signatureRequestPasscode: string;
  } | null;
};

export const retrieveFirmSignatureRequestPublicV2 = (data: {
  signatureRequestPasscode: string;
}) =>
  publicFastApiAxiosInstance
    .get<
      APIResponse<FirmSignatureRequestPublicV2Data>
    >(`firms/v2/signature-requests/${data.signatureRequestPasscode}`)
    .then((res) => res.data);

export const getFirmSignatureRequestPublicFileUrlV2 = (data: {
  clientMethod: "get_object" | "put_object";
  fileName?: string;
  key?: string;
  signatureRequestPasscode: string;
}) =>
  publicFastApiAxiosInstance
    .post<
      APIResponse<{ key: string; presignedUrl: string }>
    >("firms/v2/signature-requests/files/pre-signed-url", data)
    .then((res) => res.data);

export const deleteFirmSignatureRequestV2 = (data: {
  signatureRequestId: string;
}) =>
  authenticatedFastApiAxiosInstance.delete(
    `firms/v2/signature-requests/${data.signatureRequestId}`,
  );

export type FirmSignatureRequestFulfillmentV2Payload = {
  signatureRequestPasscode: string;
  signatureRequestDateModified: string;
  connectionDateModified: string;
  signatureRequestFilePresignedUrlKey: string;
  filePresignedUrlKey: string;
  variableValues: Record<string, string>;
};

export const fulfillFirmSignatureRequestPublicV2 = (
  data: FirmSignatureRequestFulfillmentV2Payload,
) =>
  publicFastApiAxiosInstance
    .patch<
      APIResponse<Record<string, unknown>>
    >("firms/v2/signature-requests/fulfill", data)
    .then((res) => res.data);
